import { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import AnalyticsApiService, { UserOnboardingAnalytics } from '../../services/analyticsApi';

interface UserOnboardingChartProps {
  className?: string;
}

export function UserOnboardingChart({ className }: UserOnboardingChartProps) {
  const [data, setData] = useState<UserOnboardingAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [granularity, setGranularity] = useState<'day' | 'week'>('day');

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const analytics = await AnalyticsApiService.getUserOnboardingAnalytics();
      console.log('User onboarding analytics received:', analytics);
      setData(analytics);
    } catch (error) {
      console.error('Failed to fetch user onboarding analytics:', error);
      setError('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-refresh every minute
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData();
    }, 60000); // 1 minute
    return () => clearInterval(interval);
  }, []);

  // Get data based on granularity
  const chartData = data ? (granularity === 'day' ? data.daily : data.weekly) : [];
  const summary30d = data?.summary_30d;
  const summaryAllTime = data?.summary_all_time;

  // Format date for display - manual parsing to avoid timezone bugs
  const formatXAxis = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    
    if (granularity === 'week') {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        timeZone: 'America/New_York' 
      });
    }
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      timeZone: 'America/New_York'
    });
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const [year, month, day] = label.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      const formattedDate = date.toLocaleDateString('en-US', { 
        weekday: 'short',
        month: 'short', 
        day: 'numeric',
        year: 'numeric',
        timeZone: 'America/New_York'
      });
      
      return (
        <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border border-blue-200 dark:border-blue-700 rounded-xl p-3 shadow-xl">
          <p className="font-['SF_Pro_Text',_-apple-system,_sans-serif] font-semibold text-gray-900 dark:text-gray-100 text-xs mb-2">
            {formattedDate}
          </p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="font-['SF_Pro_Display',_-apple-system,_sans-serif] text-sm" style={{ color: entry.color }}>
              <span className="font-bold">{entry.name}:</span>{' '}
              {entry.name === 'Conversion Rate' ? `${entry.value.toFixed(2)}%` : entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <Accordion type="single" collapsible className={className}>
      <AccordionItem value="user-onboarding" className="border-b-0">
        <Card className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border border-gray-200/50 dark:border-gray-800/50 shadow-xl overflow-hidden">
          <AccordionTrigger className="px-6 py-4 hover:no-underline">
            <div className="flex items-start justify-between w-full pr-4">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-green-100 dark:from-blue-900/50 dark:to-green-900/50 rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-gray-900 dark:text-gray-100 text-left">
                      User Onboarding Conversion
                    </h3>
                    <p className="text-sm font-['SF_Pro_Text',_-apple-system,_sans-serif] text-gray-600 dark:text-gray-400 text-left">
                      New user registration and onboarding completion rates
                    </p>
                  </div>
                </div>

                {/* Summary Stats (shown when collapsed) */}
                {summary30d && !isLoading && (
                  <div className="flex items-center gap-6 mt-4">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">30d Conv. Rate</p>
                      <p className="text-2xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-blue-600 dark:text-blue-400">
                        {summary30d.onboarded_rate_percent.toFixed(1)}%
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">30d Onboarded</p>
                      <p className="text-2xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-green-600 dark:text-green-400">
                        {summary30d.onboarded_users.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">30d New Users</p>
                      <p className="text-2xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-gray-700 dark:text-gray-300">
                        {summary30d.new_users.toLocaleString()}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </AccordionTrigger>

          <AccordionContent>
            <div className="px-6 pb-6">
              {/* Controls */}
              <div className="flex justify-end mb-4">
                <div className="flex bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
                  {['day', 'week'].map((g) => (
                    <button
                      key={g}
                      onClick={() => setGranularity(g as 'day' | 'week')}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        granularity === g
                          ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                      }`}
                    >
                      {g === 'day' ? 'Daily' : 'Weekly'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Main Chart */}
              <div className="mb-6">
                {isLoading ? (
                  <Skeleton className="h-80 w-full bg-gray-200/60 dark:bg-gray-700/60 rounded-xl" />
                ) : error ? (
                  <div className="h-80 flex items-center justify-center">
                    <p className="text-red-500 dark:text-red-400">{error}</p>
                  </div>
                ) : chartData.length === 0 ? (
                  <div className="h-80 flex items-center justify-center">
                    <p className="text-gray-500 dark:text-gray-400">No data available</p>
                  </div>
                ) : (
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.3} vertical={false} />
                        <XAxis 
                          dataKey={granularity === 'day' ? 'day' : 'week'}
                          tickFormatter={formatXAxis}
                          tick={{ fontSize: 11, fill: '#9ca3af', fontFamily: 'SF Pro Text, -apple-system, sans-serif' }}
                          stroke="#e5e7eb"
                        />
                        <YAxis 
                          yAxisId="left"
                          tick={{ fontSize: 11, fill: '#9ca3af', fontFamily: 'SF Pro Text, -apple-system, sans-serif' }}
                          stroke="#e5e7eb"
                        />
                        <YAxis 
                          yAxisId="right"
                          orientation="right"
                          domain={[0, 100]}
                          tickFormatter={(value) => `${value}%`}
                          tick={{ fontSize: 11, fill: '#9ca3af', fontFamily: 'SF Pro Text, -apple-system, sans-serif' }}
                          stroke="#e5e7eb"
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend 
                          wrapperStyle={{ 
                            fontFamily: 'SF Pro Text, -apple-system, sans-serif',
                            fontSize: '12px'
                          }}
                        />
                        <Bar 
                          yAxisId="left"
                          dataKey="new_users" 
                          fill="#3b82f6" 
                          name="New Users"
                          radius={[8, 8, 0, 0]}
                          opacity={0.8}
                        />
                        <Bar 
                          yAxisId="left"
                          dataKey="onboarded_users" 
                          fill="#10b981" 
                          name="Onboarded Users"
                          radius={[8, 8, 0, 0]}
                          opacity={0.8}
                        />
                        <Line 
                          yAxisId="right"
                          type="monotone" 
                          dataKey="onboarded_rate_percent" 
                          stroke="#8b5cf6" 
                          strokeWidth={3}
                          name="Conversion Rate"
                          dot={{ r: 4, fill: '#8b5cf6', strokeWidth: 2, stroke: '#ffffff' }}
                          activeDot={{ r: 6, fill: '#8b5cf6' }}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              {/* Summary Cards */}
              {summary30d && summaryAllTime && !isLoading && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    {/* 30-Day Summary */}
                    <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200 dark:border-blue-700 p-4">
                      <h4 className="text-sm font-['SF_Pro_Display',_-apple-system,_sans-serif] font-semibold text-blue-900 dark:text-blue-100 mb-3">
                        Last 30 Days
                      </h4>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-blue-700 dark:text-blue-300">New Users:</span>
                          <span className="text-lg font-bold text-blue-900 dark:text-blue-100">
                            {summary30d.new_users.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-blue-700 dark:text-blue-300">Onboarded:</span>
                          <span className="text-lg font-bold text-green-700 dark:text-green-400">
                            {summary30d.onboarded_users.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-blue-200 dark:border-blue-700">
                          <span className="text-xs text-blue-700 dark:text-blue-300">Conversion Rate:</span>
                          <span className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                            {summary30d.onboarded_rate_percent.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    </Card>

                    {/* All-Time Summary */}
                    <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200 dark:border-purple-700 p-4">
                      <h4 className="text-sm font-['SF_Pro_Display',_-apple-system,_sans-serif] font-semibold text-purple-900 dark:text-purple-100 mb-3">
                        All Time
                      </h4>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-purple-700 dark:text-purple-300">New Users:</span>
                          <span className="text-lg font-bold text-purple-900 dark:text-purple-100">
                            {summaryAllTime.new_users.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-purple-700 dark:text-purple-300">Onboarded:</span>
                          <span className="text-lg font-bold text-green-700 dark:text-green-400">
                            {summaryAllTime.onboarded_users.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-purple-200 dark:border-purple-700">
                          <span className="text-xs text-purple-700 dark:text-purple-300">Conversion Rate:</span>
                          <span className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                            {summaryAllTime.onboarded_rate_percent.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    </Card>
                  </div>

                  {/* Info Card */}
                  <Card className="bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 p-4">
                    <h4 className="font-['SF_Pro_Display',_-apple-system,_sans-serif] font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      About Onboarding Metrics
                    </h4>
                    <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                      <li className="flex items-start gap-2">
                        <span className="text-blue-500 mt-0.5">•</span>
                        <span><strong>New Users:</strong> Total user registrations in the period</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-500 mt-0.5">•</span>
                        <span><strong>Onboarded Users:</strong> Users who completed the onboarding flow</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-500 mt-0.5">•</span>
                        <span><strong>Conversion Rate:</strong> Percentage of new users who completed onboarding</span>
                      </li>
                    </ul>
                  </Card>
                </>
              )}
            </div>
          </AccordionContent>
        </Card>
      </AccordionItem>
    </Accordion>
  );
}

